import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShoppingCart, ExternalLink, Search, Filter, Eye, Package, RefreshCw } from "lucide-react";

// Make sure this matches your environment or falls back correctly
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://127.0.0.1:8000";
const API = `${BACKEND_URL}/api`;

const PurchaseOrdersPage = () => {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [selectedPO, setSelectedPO] = useState(null);
  const [selectedPOItems, setSelectedPOItems] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);

  useEffect(() => {
    fetchPurchaseOrders();
  }, []);

  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);
      // Use axios so the auth token is sent (AuthContext sets Authorization header)
      const response = await axios.get(`${API}/purchase-orders`);
      setPurchaseOrders(response.data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching purchase orders:", err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeVariant = (status) => {
    switch (status?.toLowerCase()) {
      case "pending receipt":
        return "secondary";
      case "fully billed":
        return "default";
      case "approved by supervisor/pending receipt":
        return "outline";
      default:
        return "secondary";
    }
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const userTimezoneOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() + userTimezoneOffset).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredOrders = purchaseOrders.filter(order => {
    // Search filter - check PO number and vendor name
    const searchLower = searchTerm.toLowerCase().trim();
    const matchesSearch = !searchLower || 
      (order.po_number?.toLowerCase() || '').includes(searchLower) ||
      (order.vendor_name?.toLowerCase() || '').includes(searchLower);

    // Status filter
    const matchesStatus = !statusFilter || 
      statusFilter === "all" || 
      statusFilter === "" ||
      order.status === statusFilter;
    
    // Vendor filter
    const matchesVendor = !vendorFilter || 
      vendorFilter === "all" || 
      vendorFilter === "" ||
      order.vendor_name === vendorFilter;

    return matchesSearch && matchesStatus && matchesVendor;
  });

  const uniqueStatuses = [...new Set(purchaseOrders.map(order => order.status).filter(Boolean))];
  const uniqueVendors = [...new Set(purchaseOrders.map(order => order.vendor_name).filter(Boolean))];

  // In Purchase order page.jsx

  const handleSyncPurchaseOrders = async () => {
    setSyncLoading(true);
    try {
      const response = await fetch(`${API}/sync/purchase-orders`, { 
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        // Wait slightly longer for n8n to process and the webhook to hit the backend
        setTimeout(fetchPurchaseOrders, 3000); 
      } else {
        console.error("Sync trigger failed:", response.statusText);
      }
    } catch (error) {
      console.error("Error triggering sync:", error);
    } finally {
      setSyncLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Purchase Orders</h1>
          <p className="text-gray-600">Manage purchase orders for raw materials and packaging</p>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading purchase orders...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Purchase Orders</h1>
          <p className="text-gray-600">Manage purchase orders for raw materials and packaging</p>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="text-center py-12 text-red-600">
              <p className="text-lg font-medium mb-2">Error loading purchase orders</p>
              <p className="text-sm">{error}</p>
              <Button onClick={fetchPurchaseOrders} className="mt-4">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Purchase Orders</h1>
        <p className="text-gray-600">Manage purchase orders for raw materials and packaging</p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Search className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <Input
                placeholder="Search PO number or vendor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1"
              />
            </div>
            <div className="flex flex-wrap gap-3 items-center lg:flex-shrink-0">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {uniqueStatuses.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <Select value={vendorFilter} onValueChange={setVendorFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filter by vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Vendors</SelectItem>
                    {uniqueVendors.map(vendor => (
                      <SelectItem key={vendor} value={vendor}>{vendor}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
                setVendorFilter("all");
              }}
              variant="outline"
              size="sm"
              className="whitespace-nowrap"
            >
              Clear Filters
            </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Purchase Orders Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Purchase Orders ({filteredOrders.length})
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleSyncPurchaseOrders}
                disabled={syncLoading}
                size="sm"
                variant="outline"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${syncLoading ? 'animate-spin' : ''}`} />
                {syncLoading ? 'Syncing...' : 'Sync POs'}
              </Button>
              <Button onClick={fetchPurchaseOrders} size="sm">
                Refresh
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredOrders.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium mb-2">No purchase orders found</p>
              <p className="text-sm">
                {purchaseOrders.length === 0
                  ? "No purchase orders have been synced yet."
                  : "Try adjusting your filters."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-32">PO Number</TableHead>
                    <TableHead className="w-48">Vendor</TableHead>
                    <TableHead className="w-32">Status</TableHead>
                    <TableHead className="w-24">PO Date</TableHead>
                    <TableHead className="w-24">Receive By</TableHead>
                    <TableHead className="w-24 text-right">Total</TableHead>
                    <TableHead className="w-32">Items Count</TableHead>
                    <TableHead className="w-16">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((order, index) => (
                    // ADDED ONCLICK TO TABLE ROW
                    <TableRow 
                      key={`${order.po_number}-${index}`} 
                      onClick={() => setSelectedPO(order)}
                      className="cursor-pointer hover:bg-gray-50"
                    >
                      <TableCell className="font-medium">{order.po_number}</TableCell>
                      <TableCell className="max-w-48 truncate" title={order.vendor_name}>
                        {order.vendor_name}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(order.status)}>
                          {order.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(order.po_date)}</TableCell>
                      <TableCell>{formatDate(order.ship_date)}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(order.total)}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="link"
                          className="p-0 h-auto font-normal text-blue-600 hover:text-blue-800"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent triggering row click
                            setSelectedPOItems(order);
                          }}
                        >
                          <Package className="w-4 h-4 mr-1" />
                          {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {/* Eye Button kept for clarity but redundant with row click */}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPO(order);
                            }}
                            title="View full details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {order.netsuite_link && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(order.netsuite_link, '_blank');
                              }}
                              title="Open in NetSuite"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Full Purchase Order Details Modal */}
      <Dialog open={!!selectedPO} onOpenChange={() => setSelectedPO(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Purchase Order Details - {selectedPO?.po_number}
            </DialogTitle>
          </DialogHeader>
          {selectedPO && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">PO Number</label>
                  <p className="text-lg font-semibold">{selectedPO.po_number}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Vendor</label>
                  <p className="text-lg">{selectedPO.vendor_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <div className="mt-1">
                    <Badge variant={getStatusBadgeVariant(selectedPO.status)}>
                      {selectedPO.status}
                    </Badge>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Total Amount</label>
                  <p className="text-lg font-semibold text-green-600">{formatCurrency(selectedPO.total)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">PO Date</label>
                  <p>{formatDate(selectedPO.po_date)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Ship Date</label>
                  <p>{formatDate(selectedPO.ship_date)}</p>
                </div>
              </div>

              {selectedPO.netsuite_link && (
                <div>
                  <label className="text-sm font-medium text-gray-500">NetSuite Link</label>
                  <div className="mt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(selectedPO.netsuite_link, '_blank')}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open in NetSuite
                    </Button>
                  </div>
                </div>
              )}

              {/* UPDATED ITEMS SECTION WITH NEW COLUMNS */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Items ({selectedPO.items?.length || 0})
                </h3>
                {selectedPO.items && selectedPO.items.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Item Name</TableHead>
                          <TableHead className="text-right">Order Qty</TableHead>
                          <TableHead className="text-right">Unit Price</TableHead>
                          <TableHead className="text-right">Total Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedPO.items.map((item, index) => (
                          <TableRow key={`${selectedPO.po_number}-item-${index}`}>
                            {/* Item Name: Uses Description, falls back to SKU */}
                            <TableCell className="font-medium">
                              {item.description || item.sku}
                            </TableCell>
                            
                            {/* Order Qty: Maps directly to new backend field */}
                            <TableCell className="text-right">
                              {Number(item.quantity_ordered || 0).toLocaleString()}
                            </TableCell>
                            
                            {/* Unit Price: Maps directly to new backend field */}
                            <TableCell className="text-right">
                              {formatCurrency(item.unit_price)}
                            </TableCell>
                            
                            {/* Total Amount: Maps directly to new backend field */}
                            <TableCell className="text-right font-semibold">
                              {formatCurrency(item.line_total_amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No items found in this purchase order.</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Items Details Modal (Quick View) */}
      <Dialog open={!!selectedPOItems} onOpenChange={() => setSelectedPOItems(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Items for PO {selectedPOItems?.po_number}
            </DialogTitle>
          </DialogHeader>
          {selectedPOItems && (
            <div className="space-y-4">
               <div className="bg-gray-50 p-4 rounded-lg grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-500">Vendor</span>
                    <p className="font-semibold">{selectedPOItems.vendor_name}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-sm text-gray-500">Total</span>
                    <p className="font-semibold text-green-600">{formatCurrency(selectedPOItems.total)}</p>
                  </div>
               </div>

              {/* SAME UPDATED COLUMNS FOR QUICK VIEW */}
              {selectedPOItems.items && selectedPOItems.items.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item Name</TableHead>
                        <TableHead className="text-right">Order Qty</TableHead>
                        <TableHead className="text-right">Unit Price</TableHead>
                        <TableHead className="text-right">Total Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedPOItems.items.map((item, index) => (
                        <TableRow key={`${selectedPOItems.po_number}-item-${index}`}>
                          <TableCell className="font-medium">
                            {item.description || item.sku}
                          </TableCell>
                          <TableCell className="text-right">
                            {Number(item.quantity_ordered || 0).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(item.unit_price)}
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {formatCurrency(item.line_total_amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No items found.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseOrdersPage;